import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { PuffLoader } from 'react-spinners';

import { TransactionsTable } from 'components/BankStatements/TransactionsTable';
import StatementDetailsEditor from 'components/BankStatements/StatementDetailsEditor';
import { useToast } from 'components/BankStatements/ui/toast';
import { VerificationStatus, getTransactionCounts, getErrorMessage } from 'utils/bankStatements';
import { addTransactionAction, deleteTransactionAction, getStatementAction, updateAccountAction, updateStatementAction, updateTransationAction } from 'actions/bankStatements';
import PDFViewer from './PDFViewer';

const HeaderSection = ({ onBack, applicationId }) => {
  const headerStyles = {
    container: {
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid #E5E7EB',
      padding: '10px 24px',
    },
    content: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '100%',
      margin: '4px auto',
    },
    titleSection: {
      display: 'flex',
      gap: '24px',
      alignItems: 'center',
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#111827',
      margin: 0,
      marginBottom: '2px',
    },
    infoSection: {
      display: 'flex',
      gap: '24px',
    },
    infoLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B7280',
    },
    infoValue: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#111827',
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '6px',
      border: '1px solid #E5E7EB',
      backgroundColor: '#F9FAFB',
      color: '#374151',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      height: '36px',
    },
  };

  return (
    <div style={headerStyles.container}>
      <div style={headerStyles.content}>
        <div style={headerStyles.titleSection}>
          <h1 style={headerStyles.title}>Bank Statement Review</h1>
          <p><b>Application ID:</b> {applicationId}</p>
        </div>

        {onBack && (
          <button
            type="button"
            onClick={onBack}
            style={headerStyles.backButton}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F4F6';
              e.currentTarget.style.borderColor = '#D1D5DB';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#F9FAFB';
              e.currentTarget.style.borderColor = '#E5E7EB';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M10 12L6 8L10 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to Statements
          </button>
        )}
      </div>
    </div>
  );
};

const StatementReviewPage = ({
  statementId,
  onBack,
  applicationId,
  bankProcessorKey,
}) => {
  const dispatch = useDispatch();
  const [transactionCounts, setTransactionCounts] = useState({
    needsReview: 0,
    verified: 0,
    manuallyVerified: 0,
  });
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showNeedsReview, setShowNeedsReview] = useState(true);
  const [showVerified, setShowVerified] = useState(false);
  const [showManuallyVerified, setShowManuallyVerified] = useState(false);
  const { showToast } = useToast();
  const [processedJsonData, setProcessedJsonData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const goToTransactionPage = (transaction) => {
    if (transaction) {
      const pageNumber = (transaction.dateLocation && transaction.dateLocation.pageNumber)
        || (transaction.descriptionLocation && transaction.descriptionLocation.pageNumber)
        || (transaction.depositAmountLocation && transaction.depositAmountLocation.pageNumber)
        || (transaction.withdrawalAmountLocation && transaction.withdrawalAmountLocation.pageNumber);

      if (pageNumber && pageNumber !== currentPage) {
        setCurrentPage(pageNumber);
      }
    }
  };

  const fetchStatement = (statementId) => {
    dispatch(getStatementAction({
      entityId: applicationId,
      entityType: 'application',
      statementId,
      bankProcessorKey,
      success: (res) => {
        if (res && res.accounts) {
          setProcessedJsonData(res);

          // Process the statement data to ensure consistent types
          const processedStatement = {
            ...res,
            accounts: res.accounts.map((account) => {
              // Map transactions with correct verification status
              const mappedTransactions = (account.transactions || []).map((transaction) => {
                // Handle string enum values like "Auto", "Manual", "NeedsReview"
                let status = transaction.verificationStatus;
                if (typeof status === 'string') {
                  // Convert string enum names to numeric values
                  if (status === 'Auto') {
                    status = VerificationStatus.Auto;
                  } else if (status === 'Manual') {
                    status = VerificationStatus.Manual;
                  } else if (status === 'NeedsReview') {
                    status = VerificationStatus.NeedsReview;
                  }
                }

                return {
                  ...transaction,
                  verificationStatus: status,
                };
              });

              return {
                ...account,
                transactions: mappedTransactions,
              };
            }),
          };

          setSelectedStatement(processedStatement);

          if (processedStatement && processedStatement.accounts && processedStatement.accounts.length > 0) {
            const firstTransaction = processedStatement.accounts.flatMap((account) => account.transactions.map((transaction) => ({
              transaction,
              accountNumber: account.id,
            })))
              .find(({ transaction }) => transaction.verificationStatus === VerificationStatus.NeedsReview);

            if (firstTransaction) {
              goToTransactionPage(firstTransaction.transaction);
              setSelectedTransactionId(firstTransaction.transaction.id);
              setSelectedAccountId(firstTransaction.accountNumber.toString());
            }
          }
        }
        setIsLoading(false);
      },
      fail: (error) => {
        console.error('Error fetching JSON data:', error);
        setIsLoading(false);
      },
    }));
  }

  useEffect(() => {
    setIsLoading(true);
    if (statementId) {
      fetchStatement(statementId);
    }
  }, [statementId]);

  const handleTransactionSelect = (
    transactionId,
    accountId
  ) => {
    if (processedJsonData && processedJsonData.accounts) {
      const account = processedJsonData.accounts.find(a => a.accountNumber === accountId);
      const transaction = account && account.transactions && account.transactions.find(t=> t.id === transactionId);

      goToTransactionPage(transaction);
    }
    setSelectedTransactionId(transactionId);
    setSelectedAccountId(accountId);
  };

  // Transaction update handler
  const handleTransactionUpdate = async (updatedTransaction, isVerify = false) => {
    const transactionType = updatedTransaction.depositAmount
      ? 'Deposit'
      : updatedTransaction.withdrawalAmount
        ? 'Withdrawal'
        : 'Unknown';

    // Create transaction to send to API - convert enum to number
    const apiTransaction = {
      ...updatedTransaction,
      type: transactionType,
      accountNumber: updatedTransaction.accountNumber,
      verificationStatus:
        updatedTransaction.verificationStatus === VerificationStatus.Manual
          ? 2
          : updatedTransaction.verificationStatus === VerificationStatus.Auto
            ? 1
            : 0,
    };

    dispatch(updateTransationAction({
      entityId: applicationId,
      entityType: 'application',
      statementId,
      transactionId: updatedTransaction.id,
      bankProcessorKey,
      data: apiTransaction,
      success: () => {
        fetchStatement(statementId);
        showToast(`Transaction ${isVerify ? 'verified' : 'updated'} successfully`, 'success');
      },
      fail: (error) => {
        console.error(`Error ${isVerify ? 'verifying' : 'updating'} transaction:`, error);
        const errorMsg = getErrorMessage(error, `Failed to ${isVerify ? 'verify' : 'update'} transaction`);
        showToast(errorMsg, 'error');
      },
    }));
  };

  useEffect(() => {
    const updateCounts = async () => {
      if (selectedStatement && selectedStatement.accounts) {
        const counts = await getTransactionCounts(selectedStatement.accounts);
        setTransactionCounts(counts);
      }
    };
    updateCounts();
  }, [selectedStatement]);

  const handleAccountUpdate = async (
    accountId,
    updatedAccount
  ) => {
    console.log('StatementReviewPage handleAccountUpdate started:', {
      accountId,
      updatedAccount,
    });

    dispatch(updateAccountAction({
      entityId: applicationId,
      entityType: 'application',
      statementId,
      accountId,
      bankProcessorKey,
      data: {
        id: accountId,
        accountNumber: updatedAccount.accountNumber,
        accountType: updatedAccount.accountType,
        beginningBalance: updatedAccount.beginningBalance,
        endingBalance: updatedAccount.endingBalance,
      },
      success: () => {
        fetchStatement(statementId);
        showToast('Account updated successfully', 'success');
      },
      fail: (error) => {
        console.error('Error updating account:', error);
        const errorMsg = getErrorMessage(error, 'Failed to update account');
        showToast(errorMsg, 'error');
      },
    }))
  };

  const handleTransactionDelete = async (transactionId, accountId) => {
    if (selectedStatement) {
      dispatch(deleteTransactionAction({
        entityId: applicationId,
        entityType: 'application',
        statementId,
        transactionId,
        accountId,
        bankProcessorKey,
        success: () => {
          fetchStatement(statementId);

          // Clear selection if the deleted transaction was selected
          if (selectedTransactionId === transactionId) {
            setSelectedTransactionId(null);
            setSelectedAccountId(null);
          }

          showToast('Transaction deleted successfully', 'success');
        },
        fail: (error) => {
          console.error('Error deleting transaction:', error);
          const errorMsg = getErrorMessage(error, 'Failed to delete transaction');
          showToast(errorMsg, 'error');
        },
      }));
    }
  };

  const handleTransactionAdd = async (newTransaction) => {
    if (!selectedStatement) {
      throw new Error('No statement selected');
    }

    const accountId = parseInt(newTransaction.accountNumber || '0', 10);
    if (!accountId) {
      throw new Error('Invalid account ID');
    }

    const amount = newTransaction.type === 'Deposit'
      ? newTransaction.depositAmount
      : newTransaction.withdrawalAmount;

    if (!amount) throw new Error('Amount is required');

    const data = {
      type: newTransaction.type || 'Unknown',
      date: newTransaction.date || '',
      description: newTransaction.description || '',
      amount,
    };
    
    dispatch(addTransactionAction({
      entityId: applicationId,
      entityType: 'application',
      statementId,
      accountId,
      bankProcessorKey,
      data,
      success: () => {
        fetchStatement(statementId);
        showToast('Transaction added successfully', 'success');
      },
      fail: (error) => {
        console.error('Error adding transaction:', error);
        const errorMsg = getErrorMessage(error, 'Failed to add transaction');
        showToast(errorMsg, 'error');
      },
    }));
  };

  if (!selectedStatement) {
    return (
      <div className="flex justify-center items-center mt-16">
        <PuffLoader color="#3989E3" size={100} />
      </div>
    );
  }

  const handleBack = (e) => {
    setSelectedStatement(null);
    setSelectedTransactionId(null);
    setSelectedAccountId(null);
    setCurrentPage(1);
    
    onBack(e);
  }

  const handleOnSave = async (updatedStatement, onSuccess) => {
    dispatch(updateStatementAction({
      entityId: applicationId,
      entityType: 'application',
      statementId,
      data: updatedStatement,
      bankProcessorKey,
      success: () => {
        fetchStatement(statementId);
        onSuccess && onSuccess();
        showToast('Statement details updated successfully', 'success');
      },
      fail: (error) => {
        console.error('Error updating statement details:', error);
        const errorMsg = getErrorMessage(error, 'Failed to update statement details');
        showToast(errorMsg, 'error');
      },
    }))
  }

  return (
    <div className="review-container">
      <HeaderSection
        onBack={handleBack}
        applicationId={applicationId}
      />

      <div className="split-view">
        <div className="pdf-section">
          {isLoading ? (
            <div className="text-center">Loading...</div>
          ) : (
            <PDFViewer
              applicationId={applicationId}
              statementId={statementId}
              selectedTransactionId={selectedTransactionId}
              jsonData={processedJsonData}
              onPageChange={setCurrentPage}
              bankProcessorKey={bankProcessorKey}
              currentPage={currentPage}
            />
          )}
        </div>

        <div className="details-section">
          {selectedStatement && (
            <>
              <StatementDetailsEditor
                statement={selectedStatement}
                onSave={handleOnSave}
                showNeedsReview={showNeedsReview}
                showVerified={showVerified}
                showManuallyVerified={showManuallyVerified}
                onToggleNeedsReview={() => setShowNeedsReview(!showNeedsReview)}
                onToggleVerified={() => setShowVerified(!showVerified)}
                onToggleManuallyVerified={() => setShowManuallyVerified(!showManuallyVerified)}
                transactionCounts={transactionCounts}
              />

              <TransactionsTable
                accounts={(selectedStatement && selectedStatement.accounts || [])}
                statementId={statementId}
                selectedStatement={selectedStatement}
                onTransactionSelect={handleTransactionSelect}
                selectedTransactionId={selectedTransactionId}
                selectedAccountId={selectedAccountId}
                onTransactionUpdate={handleTransactionUpdate}
                onAccountUpdate={handleAccountUpdate}
                onStatementUpdate={setSelectedStatement}
                transactionCounts={transactionCounts}
                showNeedsReview={showNeedsReview}
                showVerified={showVerified}
                showManuallyVerified={showManuallyVerified}
                onTransactionDelete={handleTransactionDelete}
                onTransactionAdd={handleTransactionAdd}
                onBack={handleBack}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

StatementReviewPage.propTypes = {
  bankProcessorKey: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  statementId: PropTypes.string.isRequired,
  onBack: PropTypes.func,
};

export default StatementReviewPage;
